import { useMemo, useState } from 'react';

import { Box, SxProps, Theme, Typography, Collapse } from '@mui/material';
import ReactMarkdown from 'react-markdown';

import { ActionTabs, ActionTabItem } from './ActionTabs';
import { CareerTrackRadio } from './CareerTrackRadio';
import { appStrings } from '../assets/appStrings';
import { TRACK_CODE_TO_NAME, TIER_TO_CAREER_TRACK_NAME } from '../models/careerPath';
import { TrackCode } from '../models/careerTrack';

const STRINGS = appStrings.buttonText;
const ACTION_TAB_STRINGS = appStrings.modules.actionTabs;

type CareerTrackCardProps = Readonly<{
  code: TrackCode;
  subtitle: string;
  description: string;
  onBookmark?: () => void;
  onShare?: () => void;
  onAddTask?: () => void;
  hasBookmarked?: boolean;
  hideActions?: boolean;
}>;

export const CareerTrackCard = ({
  code,
  subtitle,
  description,
  onBookmark,
  onShare,
  onAddTask,
  hasBookmarked,
  hideActions = false,
}: CareerTrackCardProps) => {
  const [expanded, setExpanded] = useState(false);

  const actions = useMemo<ActionTabItem[]>(() => {
    const a: ActionTabItem[] = [];

    if (onBookmark)
      a.push({
        icon: hasBookmarked ? 'BookmarkCheck' : 'Bookmark',
        onClick: onBookmark,
        tooltipHoverText: ACTION_TAB_STRINGS.bookmark,
      });
    if (onShare)
      a.push({
        icon: 'Share06',
        onClick: onShare,
        tooltipHoverText: ACTION_TAB_STRINGS.share,
      });
    if (onAddTask)
      a.push({
        icon: 'ClipboardPlus',
        onClick: onAddTask,
        tooltipHoverText: ACTION_TAB_STRINGS.task,
      });

    return a;
  }, [onBookmark, hasBookmarked, onShare, onAddTask]);

  const careerTrack = TRACK_CODE_TO_NAME[code];
  const careerTrackName = TIER_TO_CAREER_TRACK_NAME[careerTrack];

  return (
    <Box sx={cardContainerSx(hideActions)}>
      <Box sx={headercontainerSx}>
        <CareerTrackRadio careerTrack={careerTrackName} width={30} />

        {!hideActions && (
          <ActionTabs actions={actions} borderColor="Colors/Border/border-primary" />
        )}
      </Box>

      <Box sx={contentContainerSx}>
        <Box sx={textContainerSx}>
          <Typography
            variant="text-sm-medium"
            sx={{ color: ({ backrs }) => backrs.colors['Colors/Text/text-tertiary'] }}
          >
            {subtitle}
          </Typography>

          <Typography variant="display-xs-semibold" sx={trackNameSx}>
            {careerTrack}
          </Typography>

          <Collapse in={expanded} collapsedSize={0} timeout={250}>
            <Typography
              id={careerTrack}
              variant="text-sm-regular"
              sx={{
                color: ({ backrs }) => backrs.colors['Colors/Text/text-tertiary'],
                mt: ({ backrs }) => backrs.spacing['spacing-sm'],
              }}
            >
              <ReactMarkdown>{description}</ReactMarkdown>
            </Typography>
          </Collapse>
        </Box>
        <Typography
          component="span"
          variant="text-sm-semibold"
          color={(t) => t.backrs.colors['Colors/Foreground/fg-brand-primary']}
          onClick={() => setExpanded((prev) => !prev)}
          sx={readMoreBtnBaseSx}
        >
          {expanded ? STRINGS.readLess : STRINGS.readMore}
        </Typography>
      </Box>
    </Box>
  );
};

const cardContainerSx =
  (hideBeforeBorder: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    borderRadius: backrs.radius['radius-xl'],
    padding: backrs.spacing['spacing-xl'],
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    boxSizing: 'border-box',
    border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary-hover']}`,
    boxShadow: backrs.shadows['shadow-xs'],
    width: '100%',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: backrs.radius['radius-xl'],
      pointerEvents: 'none',
      ...(!hideBeforeBorder && {
        border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
      }),
    },
  });

const headercontainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',
  alignItems: 'flex-start',
};

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  width: '100%',
});

const textContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  width: '100%',
});

const readMoreBtnBaseSx: SxProps<Theme> = ({ backrs }) => ({
  alignSelf: 'flex-end',
  mt: backrs.spacing['spacing-sm'],
  cursor: 'pointer',
});

const trackNameSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-xs'],
});
